import { Loader } from '@googlemaps/js-api-loader';
import { environment } from '@internal/environment/environment';
import { translations } from '@internal/i18n/translations';
import customers from '@internal/utils/geoLocation/customers.json';
import { getGeoLocation } from '@internal/utils/geoLocation/getGeoLocation';
import { localeNamespace, TLocale } from '@internal/utils/machine/i18n/Locale';
import { DefaultQueryParams } from '@internal/utils/routing/urls/DefaultQueryParams';
import { tracker } from '@internal/utils/tracking/tracker';
import { TrustPilot } from '@renderer-ui-library/molecules';
import { Button, Typography } from '@renderer-ui-library/mui';
import classNames from 'classnames';
import { uniqBy } from 'lodash';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import styles from './customerMap.module.scss';

const { NEXT_PUBLIC_GOOGLE_MAPS_API_KEY } = environment;

// this is just some id for google maps
const mapId = '0c50c5c1-06cd-45b6-8cba-57959b6cece9';

interface Props {
    locale: TLocale;
    mapWidth: number;
}

const getStaticMapUrl = ({
    locale,
    mapWidth,
    cdn,
}: {
    locale: TLocale;
    mapWidth: number;
    cdn: boolean;
}) => {
    // increase this number to invalidate the cdn cache
    const mapVersion = 1;

    // cdn configuration: https://imagekit.io/dashboard/url-endpoints/2lFmMio0E
    const baseUrl = cdn
        ? 'https://ik.imagekit.io/efarm/website-api'
        : `${environment.OWN_URL}/api`;
    return `${baseUrl}/happyCustomersMap?locale=${locale}&width=${mapWidth}&height=150&v=${mapVersion}`;
};

export const CustomerMap: React.FC<Props> = memo(({ locale, mapWidth }) => {
    const [isMapExpanded, setMapExpanded] = useState(false);
    const [showJsMap, setShowJsMap] = useState(false);
    const { query } = useRouter();

    const bypassCdn = !!(query as DefaultQueryParams).bypassCdn;

    const handleCtaClick = useCallback(() => {
        setMapExpanded(prev => !prev);
        setShowJsMap(true);

        tracker.trackEvent({
            name: 'click',
            target_name: 'block.happy_customer_map',
            is_interaction: true,
        });
    }, []);

    const { t } = useTranslation(localeNamespace.common);
    const containerRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        if (!showJsMap) {
            return;
        }

        const fn = async () => {
            if (!containerRef.current) {
                return;
            }

            const loader = new Loader({
                // biome-ignore lint/style/noNonNullAssertion: legacy
                apiKey: NEXT_PUBLIC_GOOGLE_MAPS_API_KEY!,
                version: 'weekly',
            });

            const { Map: GoogleMap } = await loader.importLibrary('maps');
            const { AdvancedMarkerElement } =
                await loader.importLibrary('marker');

            const map = new GoogleMap(containerRef.current, {
                mapId,
                zoom: 7,
                maxZoom: 10,
            });

            const center = getGeoLocation(locale);

            map.setCenter({ lat: center.lat, lng: center.lng });

            uniqBy(
                customers,
                customer => `${customer.lat},${customer.lng}`,
            ).map(({ lat, lng }) => {
                const marker = document.createElement('div');
                marker.className = styles.marker;

                return new AdvancedMarkerElement({
                    map,
                    position: {
                        lat: Math.round(lat * 100) / 100,
                        lng: Math.round(lng * 100) / 100,
                    },
                    content: marker,
                });
            });
        };

        fn();
    }, [showJsMap, locale]);

    return (
        <>
            <div
                className={classNames(styles.mapContainer, {
                    [styles.mapContainerExpanded]: isMapExpanded,
                })}
            >
                <div
                    ref={containerRef}
                    className={classNames(styles.map, {
                        [styles.mapExpanded]: isMapExpanded,
                    })}
                    style={{
                        backgroundImage: `url('${getStaticMapUrl({
                            locale,
                            mapWidth,
                            cdn: !bypassCdn,
                        })}')`,
                    }}
                />
                <div
                    className={classNames(styles.mapBackground, {
                        [styles.mapBackgroundExpanded]: isMapExpanded,
                    })}
                />

                <Button
                    className={classNames(styles.mapTitleWrapper, {
                        [styles.mapTitleWrapperExpanded]: isMapExpanded,
                    })}
                    onClick={handleCtaClick}
                    size="small"
                >
                    <Typography
                        variant="h6"
                        color="primary"
                        className={styles.happyCustomerCta}
                        component="span"
                    >
                        {t(translations.happyCustomerCta)}
                    </Typography>
                </Button>
            </div>

            <div className={styles.trustPilotWrapper}>
                <TrustPilot theme="light" locale={locale} variant="small" />
            </div>
        </>
    );
});

CustomerMap.displayName = 'CustomerMap';
